<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import MyQaIncidentsAPI from 'dashboard/api/myQaIncidents';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
// Same labels the QA-side Live Monitor uses. This page was rendering the raw
// enum — an agent read "flagged" and had to guess whether it meant they had done
// something wrong. One source of truth so the two surfaces cannot drift.
import { verdictLabel, reviewStateLabel } from '../helper/incidentReview';
import '../styles/qaShell.css';

const MY_QA_V1 = 'MY_QA_V1';
const { t } = useI18n();
const route = useRoute();

// Same URL builder the QA-side Live Monitor uses, so the two surfaces open the
// same place.
const conversationLink = incident => {
  const id = incident?.conversation_display_id;
  return id
    ? frontendURL(conversationUrl({ accountId: route.params.accountId, id }))
    : null;
};

const isLoading = ref(false);
const errorText = ref('');
const incidents = ref([]);
const appealingId = ref(null);
const reasons = reactive({});
const busyId = ref(null);

const load = async () => {
  isLoading.value = true;
  errorText.value = '';
  try {
    const { data } = await MyQaIncidentsAPI.get();
    incidents.value = data.incidents || [];
  } catch (error) {
    errorText.value = t('QA_DASHBOARD.MY_QA.ERROR_LOAD');
    useAlert(errorText.value);
  } finally {
    isLoading.value = false;
  }
};

const startAppeal = id => {
  appealingId.value = id;
  if (!(id in reasons)) reasons[id] = '';
};

const submitAppeal = async incident => {
  const reason = (reasons[incident.id] || '').trim();
  if (!reason) {
    useAlert(t('QA_DASHBOARD.MY_QA.REASON_REQUIRED'));
    return;
  }
  busyId.value = incident.id;
  try {
    await MyQaIncidentsAPI.appeal(incident.id, reason);
    useAlert(t('QA_DASHBOARD.MY_QA.SUBMITTED'));
    appealingId.value = null;
    delete reasons[incident.id];
    await load();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('QA_DASHBOARD.MY_QA.ERROR_ACTION')
    );
  } finally {
    busyId.value = null;
  }
};

const verdictTone = incident =>
  incident.human_verdict === 'confirmed' ? 'qa-badge--bad' : 'qa-badge--muted';

onMounted(load);
</script>

<template>
  <div class="qa-page" :data-qa-compile-marker="MY_QA_V1">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.MY_QA.TITLE') }}</h1>
          <p class="qa-desc">{{ t('QA_DASHBOARD.MY_QA.DESCRIPTION') }}</p>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="load"
          >
            {{ t('QA_DASHBOARD.MY_QA.REFRESH') }}
          </button>
        </div>
      </div>

      <div class="qa-pill qa-pill--muted my-qa-note">
        {{ t('QA_DASHBOARD.MY_QA.OBSERVE_NOTE') }}
      </div>

      <div class="qa-content">
        <p v-if="errorText" class="qa-error">{{ errorText }}</p>

        <div v-if="!isLoading && !incidents.length" class="qa-card">
          <div class="qa-card-body">
            <h3 class="qa-card-title">
              {{ t('QA_DASHBOARD.MY_QA.EMPTY_TITLE') }}
            </h3>
            <p class="qa-desc">{{ t('QA_DASHBOARD.MY_QA.EMPTY_BODY') }}</p>
          </div>
        </div>

        <div v-else class="qa-card">
          <div class="qa-card-body qa-card-body--flush">
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.MY_QA.TH_ISSUE') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.MY_QA.TH_CONVERSATION') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.MY_QA.TH_SEVERITY') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.MY_QA.TH_VERDICT') }}
                    </th>
                    <th class="qa-th qa-th--center">
                      {{ t('QA_DASHBOARD.MY_QA.TH_STATUS') }}
                    </th>
                    <th class="qa-th">
                      {{ t('QA_DASHBOARD.MY_QA.TH_ACTION') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="incident in incidents" :key="incident.id">
                    <td class="qa-td">
                      <div>{{ incident.title || incident.issue_type }}</div>
                      <div v-if="incident.summary" class="qa-subtle">
                        {{ incident.summary }}
                      </div>
                    </td>
                    <!-- Naming a problem without saying which chat it was leaves
                         the agent nothing to check before they decide whether to
                         appeal. -->
                    <td class="qa-td">
                      <a
                        v-if="conversationLink(incident)"
                        :href="conversationLink(incident)"
                        class="qa-link"
                      >
                        #{{ incident.conversation_display_id }}
                      </a>
                      <span v-else class="qa-subtle">—</span>
                    </td>
                    <td class="qa-td qa-th--center">{{ incident.severity }}</td>
                    <td class="qa-td qa-th--center">
                      <span class="qa-badge" :class="verdictTone(incident)">
                        {{
                          incident.human_verdict || incident.machine_verdict
                            ? verdictLabel(
                                incident.human_verdict ||
                                  incident.machine_verdict
                              )
                            : '—'
                        }}
                      </span>
                    </td>
                    <td class="qa-td qa-th--center">
                      <span
                        v-if="incident.open_appeal"
                        class="qa-badge qa-badge--warn"
                      >
                        {{
                          t('QA_DASHBOARD.MY_QA.APPEAL_STATUS', {
                            status: incident.open_appeal.status,
                          })
                        }}
                      </span>
                      <span v-else class="qa-subtle">
                        {{ reviewStateLabel(incident.review_state) }}
                      </span>
                    </td>
                    <td class="qa-td">
                      <template v-if="incident.open_appeal">
                        <span class="qa-subtle">{{
                          t('QA_DASHBOARD.MY_QA.ALREADY_APPEALED')
                        }}</span>
                      </template>
                      <template v-else-if="!incident.appealable">
                        <span class="qa-subtle">—</span>
                      </template>
                      <template v-else-if="appealingId === incident.id">
                        <div class="qa-actions qa-actions--compact">
                          <input
                            v-model="reasons[incident.id]"
                            type="text"
                            class="qa-input"
                            :placeholder="
                              t('QA_DASHBOARD.MY_QA.REASON_PLACEHOLDER')
                            "
                          />
                          <button
                            type="button"
                            class="qa-btn qa-btn--sm qa-btn--primary"
                            :disabled="busyId === incident.id"
                            @click="submitAppeal(incident)"
                          >
                            {{ t('QA_DASHBOARD.MY_QA.SUBMIT') }}
                          </button>
                          <button
                            type="button"
                            class="qa-btn qa-btn--sm qa-btn--ghost"
                            @click="appealingId = null"
                          >
                            {{ t('QA_DASHBOARD.MY_QA.CANCEL') }}
                          </button>
                        </div>
                      </template>
                      <template v-else>
                        <button
                          type="button"
                          class="qa-btn qa-btn--sm"
                          @click="startAppeal(incident.id)"
                        >
                          {{ t('QA_DASHBOARD.MY_QA.APPEAL') }}
                        </button>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The note sits above .qa-content, straight off .qa-shell, so it had no inset
   of its own and started at the card border. 24px lines it up with the title
   in .qa-header rather than with the table below it, which .qa-content insets
   by 32px — the same pairing every other QA page uses.

   Vertically it had nothing above it either and sat right on the header's
   bottom rule. 16px is what .qa-content puts between that rule and a page's
   first block everywhere else, so the note now clears the divider by exactly
   as much as ordinary content does. Bottom is 0 because .qa-content's own
   16px padding-top follows immediately — that already spaces the note off the
   table, and keeping the old 12px on top of it would total 28px and leave the
   note visibly closer to the rule than to the table. */
.my-qa-note {
  margin: 16px 24px 0;
}

/* Matches the breakpoint qaShell.css uses to pull .qa-header in to 16px. */
@media (max-width: 1100px) {
  .my-qa-note {
    margin-left: 16px;
    margin-right: 16px;
  }
}
</style>
